import { useEffect, useMemo, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, CircularProgress, InputAdornment, Stack, Tab, Tabs, TextField, Typography, useTheme } from "@mui/material";
import { DekoxIcon } from "src/components";
import { useFaqPageT } from "src/translation-hooks/faq-page";
import { faqActions } from "./faq-store";
import { RootState } from "src/store";
import { connect } from "react-redux";
import { hasValueAndNotEmpty } from "@itk/typescript-utils";
import { Model_FaqCategoryGet } from "src/backend/openapi/v1";
import { useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { useGetContactPersonDetail } from "src/hooks";
import parse from "html-react-parser";

const ownActions = { getFaqRequestByLanguageId: faqActions.getFaqRequestByLanguageId };

const mapStateToProps = (state: RootState) => ({
  faqCategory: state.pageReducer.faqReducer.faqCategory,
  loadedDataLanguageId: state.pageReducer.faqReducer.loadedDataLanguageId
});

type Props = ReturnType<typeof mapStateToProps> & typeof ownActions;

export const FAQInfoBase = (props: Props) => {
  const { faqCategory, getFaqRequestByLanguageId: getLanguageRequestById, loadedDataLanguageId } = props;
  const faqPageT = useFaqPageT();
  const { contactPersonDetail } = useGetContactPersonDetail();

  const genericTranslationsT = useGenericTranslationsT();

  useEffect(() => {
    if (
      hasValueAndNotEmpty(contactPersonDetail) &&
      hasValueAndNotEmpty(contactPersonDetail?.languageId) &&
      (faqCategory.loading === "not-started" || loadedDataLanguageId !== contactPersonDetail.languageId)
    )
      getLanguageRequestById(contactPersonDetail?.languageId);
  }, [contactPersonDetail?.languageId, faqCategory]);

  const isLoading = faqCategory.loading === "is-loading";

  const faqCategoryData = useMemo((): Model_FaqCategoryGet[] => (faqCategory.loading === "has-loaded" ? faqCategory.data : []), [faqCategory]);

  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTab, setSelectedTab] = useState(0);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const filteredFaqData = useMemo(() => {
    if (!searchQuery.trim()) return faqCategoryData.sort((x, y) => x.sortOrder - y.sortOrder);

    return faqCategoryData
      .map(category => ({
        ...category,
        questions: category.questions
          .filter(q => q.question.toLowerCase().includes(searchQuery.toLowerCase()) || q.answer.toLowerCase().includes(searchQuery.toLowerCase()))
          .sort((x, y) => x.sortOrder - y.sortOrder)
      }))
      .sort((x, y) => x.sortOrder - y.sortOrder);
  }, [searchQuery, faqCategoryData]);

  if (isLoading) {
    return (
      <Stack flex={1} alignItems="center" justifyContent="center" paddingY={4}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Stack flex={1}>
      <Stack direction="row" gap={4}>
        {/* Left side section */}
        <Box color={theme.palette.customColors.darkGrey}>
          <Typography variant="h2" mb={3}>
            {faqPageT.faq}
          </Typography>
          <Tabs
            orientation="vertical"
            value={selectedTab}
            onChange={handleTabChange}
            sx={{
              "& .MuiTab-root": {
                alignItems: "flex-start",
                width: 300
              },
              "& .MuiTabs-indicator": {
                display: "none"
              }
            }}
          >
            {filteredFaqData.map((section, index) => (
              <Tab
                sx={{
                  borderBottom: `2px solid ${theme.palette.customColors.border}`,
                  fontWeight: theme.typography.fontWeightBold,
                  textTransform: "none"
                }}
                key={section.categoryId}
                label={section.description}
                id={`tab-${index}`}
              />
            ))}
          </Tabs>
        </Box>

        {/* Right side section */}
        <Stack width="100%">
          <Typography variant="h3" fontWeight={theme.typography.fontWeightBold} color={theme.palette.customColors.darkGrey} mb={2}>
            {faqCategoryData[selectedTab]?.description}
          </Typography>
          <TextField
            autoFocus
            placeholder={genericTranslationsT.search}
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            sx={{ mb: 3, width: 300 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: 1 }}>
                    <DekoxIcon icon="search" />
                  </InputAdornment>
                )
              }
            }}
          />

          {filteredFaqData.map((category, tabIndex) => (
            <Box role="tabpanel" hidden={selectedTab !== tabIndex} id={`tabpanel-${tabIndex}`} key={category.categoryId}>
              {selectedTab === tabIndex && (
                <Box flex={1}>
                  {category.questions.map((item, categoryId) => (
                    <Accordion
                      key={item.questionId}
                      defaultExpanded={categoryId === 0}
                      sx={{
                        mb: 1,
                        "&.Mui-expanded": {
                          borderLeft: `3px solid ${theme.palette.primary.main}`
                        },
                        width: "100%"
                      }}
                    >
                      <AccordionSummary expandIcon={<DekoxIcon icon="expand" />}>
                        <Typography color={theme.palette.primary.main} fontWeight={theme.typography.fontWeightBold}>
                          {item.question}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails color={theme.palette.customColors.darkGrey}>{parse(item.answer)}</AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              )}
            </Box>
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
};

export const FAQ = connect(mapStateToProps, ownActions)(FAQInfoBase);
